/**
 *  Functionality this file provides:
 *  A user can hover a "menu mega trigger" to activate the mega menu.
 *  This happens after a specified timeout to help prevent jank.
 *
 *  The menu triggers should use a unique data tag.
 *  This tag references the menu it should open.
 *  eg: <a href="/careers" data-mega="mega-1">Careers</a>
 *
 *  The content area that will show should be marked with a corresponding data tag.
 *  eg: <div class="mega-content" data-mega-content="mega-1">
 *
 *  The menu should stay open when either:
 *  1. Hovering the trigger
 *  2. Hovering the menu content area
 *
 *  When the user leaves one of these areas a timeout counts down if the timeout reaches 0
 *  the menu will be hidden.
 *
 * Common bugs: ( Should be resolved )
 * Enter a trigger & before the animation is finished leave the trigger
 * Enter a trigger & before the animation is finished enter a different
 *
 */

import gsap from 'gsap';
import hexToRGB from '../utilities/hexToRGB'

window.addEventListener('frame-ready', init)

function init() {

    let triggers = Array.from(document.querySelectorAll('[data-mega]'));

    if (triggers.length > 0) {

        let style = getComputedStyle(document.body)
        const header = document.querySelector('.header-desktop')
        const links = document.querySelectorAll('.header-desktop a:not(.mega-content--action), .header-desktop .search-trigger, .header-desktop .dropdown')
        const linkStyle = getComputedStyle(document.querySelector('.header-desktop--main-content a:not(.main-content--logo)'))
        const main = document.querySelector('.header-desktop--main')
        const mega = document.querySelector('.header-desktop--mega')
        const searchTrigger = document.querySelector('.search-trigger')

        // Set what height the mega parent needs to be when child is active
        mega.querySelectorAll('.mega-content').forEach(content => {
            content.setAttribute('data-height', parseInt(content.getBoundingClientRect().height))
        })

        let duration = .2
        let ease = "power2.inOut"

        let activeMenu = null // Is there a menu active
        let keepAlive = false
        let animating = false

        let closeTimeout
        let openTimeout

        mega.addEventListener('start', () => { animating = true })
        mega.addEventListener('stop', () => { animating = false })

        let megas = triggers.map(trigger => {

            const key = trigger.getAttribute('data-mega')
            const content = document.querySelector(`[data-mega-content="${key}"]`)
            const blocks = content.querySelectorAll('[class*="col"]')

            let height = parseInt(content.getAttribute('data-height'))
            let offset = header.getBoundingClientRect().height + 'px'

            window.addEventListener('resize', () => {
                height = parseInt(content.getAttribute('data-height'))
                offset = header.getBoundingClientRect().height + 'px'
            })

            if (content == null) throw new Error(`Mega menu can not operate without content for ${key}.`)

            // Make sure nothing is clickable
            content.style.pointerEvents = 'none'

            return {
                key,
                trigger,
                content,
                blocks,
                height,
                offset,
            }
        })

        megas.forEach(menu => {
            searchTrigger.addEventListener('click', () => {
                keepAlive = true
            })

            // When entering content area stop the closing of the mega menu
            menu.content.addEventListener('mouseenter', () => {
                //activeMenu = false
                clearTimeout(openTimeout)
                clearTimeout(closeTimeout)
            })

            menu.content.addEventListener('mouseleave', () => {
                clearTimeout(openTimeout)
                closeTimeout = setTimeout(() => {
                    //activeMenu = true
                    close(menu)
                }, 500)
            })

            menu.trigger.addEventListener('mouseenter', () => {
                clearTimeout(closeTimeout)
                openTimeout = setTimeout(() => {
                    open(menu)
                }, 500)

            })

            menu.trigger.addEventListener('mouseleave', () => {
                clearTimeout(openTimeout)
                closeTimeout = setTimeout(() => {
                    close(menu)
                }, 500)
            })
        })

        function show_from_hidden(menu) {
            let { height, offset, content } = menu

            const event = document.createEvent('Event')
            event.initEvent('start', true, true)
            mega.dispatchEvent(event)

            let tl = gsap.timeline({ onComplete: () => {
                    activeMenu = menu

                    const event = document.createEvent('Event')
                    event.initEvent('stop', true, true)
                    mega.dispatchEvent(event)
                }})
                .to(main, { duration, backgroundColor: hexToRGB(style.getPropertyValue('--primary').trim(), 1), ease })
                .to(links, { duration, color: style.getPropertyValue('--white'), ease }, "-=.18")
                .to(content, { duration, display: 'block' })
                .to(mega, { duration, translateY: offset, height, ease }, "-=.18")
                .to(content, { duration, autoAlpha: 1, pointerEvents: 'all', ease })
                .play()
        }

        function toggle_menus(from, to) {

            const event = document.createEvent('Event')
            event.initEvent('start', true, true)
            mega.dispatchEvent(event)

            let tl = gsap.timeline({ onComplete: () => {
                    activeMenu = to

                    const event = document.createEvent('Event')
                    event.initEvent('stop', true, true)
                    mega.dispatchEvent(event)

                }})
                .to(from.content, { duration, display: 'none' })
                .to(from.content, { duration, autoAlpha: 0, pointerEvents: 'none', ease })
                .to(mega, { duration, height: to.height, ease }, "-=.18")
                .to(to.content, { duration, display: 'block' })
                .to(to.content, { duration, autoAlpha: 1, pointerEvents: 'all', ease }, "-=.18")
                .play()
        }

        function open(menu) {
            // While the menu is 'closing' the active menu is still set so if while
            // the menu is animating and you hover over another trigger we shouldn't trigger
            // a toggle we should wait for the animation to end then open the new menu from
            // closed position
            let run = () => {
                if(!activeMenu) {
                    show_from_hidden(menu)
                } else {
                    toggle_menus(activeMenu, menu)
                }
            }

            if(!animating) {
                run()
            } else {
                // We need to poll to see when the animation finishes
                setTimeout(() => {
                    open(menu)
                }, 500)
            }
        }

        function close(menu) {

            // There was a bug similar to the 'while closing' bug. If while opening the animation hasn't finished
            // you move off the trigger or content to 'close' the menu it won't close because there was no active menu
            // set before the animation finished
            if(activeMenu && !keepAlive) {

                let { height, offset, content } = menu

                let color = hexToRGB(style.getPropertyValue('--primary').trim(), 1)
                if (document.body.classList.contains('CareerListPage') ||
                    document.body.classList.contains('CountryPage') ||
                    document.body.classList.contains('WhoWeArePage') ||
                    document.body.classList.contains('ContactPage') ||
                    document.body.classList.contains('BusinessPage') ||
                    document.body.classList.contains('NewsListPage') ||
                    document.body.classList.contains('SustainabilityListPage')) {
                        color = 'rgba(255, 255, 255, 1)'
                }

                animating = true

                const event = document.createEvent('Event')
                event.initEvent('start', true, true)
                mega.dispatchEvent(event)

                let tl = gsap.timeline({
                    onComplete: () => {
                            activeMenu = null
                            const event = document.createEvent('Event')
                            event.initEvent('stop', true, true)
                            mega.dispatchEvent(event)
                        }
                    })
                    .to(mega, { duration, translateY: -height, height: 0, ease })
                    .to(content, { duration, autoAlpha: 0, pointerEvents: 'none', ease })
                    .to(links, { duration, color, ease }, "-=.18")
                    .to(main, { duration, backgroundColor: hexToRGB(style.getPropertyValue('--primary').trim(), 0), ease })
                    .play()
            } else {
                // There is no activeMenu 'yet' it could be animating in
                if(!animating) {
                    // console.log('Not animating - no active menu')
                } else {
                    // if it's already animating we poll then close it when it's finished
                    // console.log('is animating')
                    setTimeout(() => {
                        close(menu)
                    }, 500)
                }
            }
        }
    }
}
